import React from 'react'
import { useParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import { useAuth } from '../context/AuthContext'
import WishItemCard from '../components/WishItemCard'
import {
  API_BASE_URL,
  ApiResponse,
  WishItem,
  Comment as WishComment,
  createWishItem,
  createComment as createWishComment,
  deleteWishItem,
  fetchCommentsForWishList,
  updateWishItem
} from '../utils/api'

const ITEM_PLACEHOLDERS = [
  'Squirrel Launcher',
  'Cat food',
  'Dog repellent',
  'Heated cat bed',
  'Window bird feeder',
  'Yummy mice',
  'Catnip toys',
  'Unlimited Tuna Subscription',
  'Bottomless kibble bowl',
  'Adjustable cat tower',
  'Light bulbs',
  'Fish bowl',
  'Eternal sunspot',
  'Crinkle tunnel',
  'Amazon boxes',
  'Bedside treat dispenser'
]

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '1s',
    m: '1min',
    mm: '%dmin',
    h: '1h',
    hh: '%dh',
    d: '1 day',
    dd: '%d days',
    M: '1 mo',
    MM: '%d mo',
    y: '1 yr',
    yy: '%d yr'
  }
})

const WishList: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const { user, allUsers } = useAuth()
  const [wishItems, setWishItems] = React.useState<WishItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [isAddFormOpen, setIsAddFormOpen] = React.useState(false)
  const [newItemName, setNewItemName] = React.useState('')
  const [newItemDescription, setNewItemDescription] = React.useState('')
  const [newItemUrl, setNewItemUrl] = React.useState('')
  const [newItemImageUrl, setNewItemImageUrl] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [deletingItemId, setDeletingItemId] = React.useState<string | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [togglingItemId, setTogglingItemId] = React.useState<string | null>(null)
  const [commentsByItemId, setCommentsByItemId] = React.useState<Record<string, WishComment[]>>({})
  const [commentsLoading, setCommentsLoading] = React.useState(false)
  const [commentsError, setCommentsError] = React.useState<string | null>(null)
  const [commentInputs, setCommentInputs] = React.useState<Record<string, string>>({})
  const [commentErrors, setCommentErrors] = React.useState<Record<string, string | null>>({})
  const [commentVisibility, setCommentVisibility] = React.useState<Record<string, boolean>>({})
  const [commentSubmittingId, setCommentSubmittingId] = React.useState<string | null>(null)
  const selectRandomPlaceholder = React.useCallback(() => {
    const randomIndex = Math.floor(Math.random() * ITEM_PLACEHOLDERS.length)
    return ITEM_PLACEHOLDERS[randomIndex]
  }, [])
  const [itemPlaceholder, setItemPlaceholder] = React.useState(() => selectRandomPlaceholder())
  React.useEffect(() => {
    if (isAddFormOpen) {
      setItemPlaceholder(selectRandomPlaceholder())
    }
  }, [isAddFormOpen, selectRandomPlaceholder])
  const formatCreatedTime = React.useCallback((timestamp?: number | null) => {
    if (!timestamp) {
      return null
    }

    const parsed = dayjs(timestamp)
    if (!parsed.isValid()) {
      return null
    }

    return parsed.fromNow()
  }, [])
  const displayUser = React.useMemo(() => {

    const matchById = allUsers.find((profile) => profile.id?.trim().toLowerCase() === userId)
    if (matchById) {
      return matchById
    }

    return allUsers.find((profile) => {
      const handle = profile.email?.split('@')[0]?.toLowerCase()
      return handle === userId
    }) ?? null
  }, [allUsers, userId])
  const displayName = displayUser?.shortName ?? 'Guest'
  const isOwnList = Boolean(
    (user?.id && displayUser?.id && user.id === displayUser.id)
  )
  const canSuggestItem = Boolean(user?.id) && !isOwnList
  const canSubmitItems = isOwnList || canSuggestItem

  const findProfileById = React.useCallback(
    (identifier?: string | null) => {
      if (!identifier) {
        return null
      }

      const normalized = identifier.trim().toLowerCase()
      return (
        allUsers.find((profile) => profile.id?.trim().toLowerCase() === normalized) ??
        allUsers.find((profile) => profile.email?.trim().toLowerCase() === normalized) ??
        allUsers.find((profile) => profile.email?.split('@')[0]?.toLowerCase() === normalized)
      ) ?? null
    },
    [allUsers]
  )

  const resolveDisplayName = React.useCallback(
    (identifier?: string | null) => {
      if (!identifier) {
        return 'Someone'
      }

      const profile = findProfileById(identifier)
      if (profile?.shortName) {
        return profile.shortName
      }

      if (profile?.fullName) {
        return profile.fullName
      }

      return identifier
    },
    [findProfileById]
  )

  React.useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    const trimmedUserId = userId?.trim()

    if (!trimmedUserId) {
      setWishItems([])
      setError('Missing user identifier for wish list lookup.')
      setLoading(false)
      return () => {
        cancelled = true
        controller.abort()
      }
    }

    const fetchWishItems = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetch(`${API_BASE_URL}/wishlists/${trimmedUserId}`, {
          signal: controller.signal
        })

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`)
        }

        const payload: ApiResponse<WishItem[]> = await response.json()

        if (!cancelled) {
          if (payload.success && Array.isArray(payload.data)) {
            setWishItems(payload.data)
            setActionError(null)
          } else {
            setWishItems([])
            setError(payload.error ?? 'Wish list data is unavailable.')
          }
        }
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) {
          return
        }

        console.error('Failed to fetch wish list items', err)
        setWishItems([])
        setError(err instanceof Error ? err.message : 'Unknown error fetching wish list.')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchWishItems()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [userId])

  React.useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    const trimmedUserId = userId?.trim()
    if (!trimmedUserId) {
      setCommentsByItemId({})
      setCommentsError(null)
      setCommentsLoading(false)
      return () => {
        cancelled = true
        controller.abort()
      }
    }

    setCommentsLoading(true)
    setCommentsError(null)

    const loadComments = async () => {
      try {
        const comments = await fetchCommentsForWishList(trimmedUserId, controller.signal)
        if (cancelled) {
          return
        }

        const grouped = comments.reduce<Record<string, WishComment[]>>((acc, comment) => {
          if (!comment.wishItemId) {
            return acc
          }

          const existing = acc[comment.wishItemId] ?? []
          acc[comment.wishItemId] = [...existing, comment]
          return acc
        }, {})

        Object.keys(grouped).forEach((key) => {
          grouped[key] = grouped[key].sort((a, b) => {
            const aTime = a.createdAt ?? 0
            const bTime = b.createdAt ?? 0
            return aTime - bTime
          })
        })

        setCommentsByItemId(grouped)
      } catch (err) {
        if (controller.signal.aborted || cancelled) {
          return
        }

        console.error('Failed to fetch wish list comments', err)
        setCommentsByItemId({})
        setCommentsError(err instanceof Error ? err.message : 'Unable to load comments right now.')
      } finally {
        if (!cancelled) {
          setCommentsLoading(false)
        }
      }
    }

    void loadComments()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [userId])

  const resetAddItemForm = () => {
    setNewItemName('')
    setNewItemDescription('')
    setNewItemUrl('')
    setNewItemImageUrl('')
    setFormError(null)
  }

  const handleAddItemSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedUserId = userId?.trim()

    if (!trimmedUserId) {
      return
    }

    const trimmedName = newItemName.trim()
    if (!trimmedName) {
      setFormError('Item name is required.')
      return
    }

    if (!isOwnList && !user?.id) {
      setFormError('You must be signed in to suggest an item.')
      return
    }

    setIsSubmitting(true)
    setFormError(null)

    try {
      const createdBy = isOwnList ? (user?.id ?? trimmedUserId) : (user?.id as string)
      const trimmedDescription = newItemDescription.trim()
      const trimmedUrl = newItemUrl.trim()
      const trimmedImageUrl = newItemImageUrl.trim()
      const payload = {
        name: trimmedName,
        description: trimmedDescription || undefined,
        url: trimmedUrl || undefined,
        imageUrl: trimmedImageUrl || undefined,
        createdBy
      }

      const createdItem = await createWishItem(trimmedUserId, payload)
      setWishItems((current) => [createdItem, ...current])
      resetAddItemForm()
      setActionError(null)
      setIsAddFormOpen(false)
    } catch (err) {
      console.error('Failed to create wish item', err)
      setFormError(err instanceof Error ? err.message : 'Unable to add item right now.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleAddForm = () => {
    if (isAddFormOpen) {
      resetAddItemForm()
    }
    setIsAddFormOpen((prev) => !prev)
  }

  const handleDeleteItem = async (itemId: string) => {
    const trimmedUserId = userId?.trim()

    if (!trimmedUserId || deletingItemId) {
      return
    }

    setDeletingItemId(itemId)
    setActionError(null)

    try {
      await deleteWishItem(trimmedUserId, itemId)
      setWishItems((current) => current.filter((item) => item.id !== itemId))
      setCommentsByItemId((current) => {
        if (!current[itemId]) {
          return current
        }
        const next = { ...current }
        delete next[itemId]
        return next
      })
      setCommentInputs((current) => {
        if (!(itemId in current)) {
          return current
        }
        const next = { ...current }
        delete next[itemId]
        return next
      })
      setCommentErrors((current) => {
        if (!(itemId in current)) {
          return current
        }
        const next = { ...current }
        delete next[itemId]
        return next
      })
      setCommentVisibility((current) => {
        if (!(itemId in current)) {
          return current
        }
        const next = { ...current }
        delete next[itemId]
        return next
      })
    } catch (err) {
      console.error('Failed to delete wish item', err)
      setActionError(err instanceof Error ? err.message : 'Unable to delete the selected item right now.')
    } finally {
      setDeletingItemId(null)
    }
  }

  const handleTogglePurchased = async (itemId: string, currentState: boolean, purchaserId?: string | null) => {
    const trimmedUserId = userId?.trim()
    if (!trimmedUserId || togglingItemId) {
      return
    }

    if (currentState && purchaserId && user?.id && purchaserId !== user.id) {
      setActionError('Only the original purchaser can unmark this item.')
      return
    }

    setTogglingItemId(itemId)
    setActionError(null)

    try {
      const updated = await updateWishItem(trimmedUserId, itemId, {
        purchased: !currentState,
        purchasedBy: !currentState ? (user?.id) : null
      })

      setWishItems((current) => current.map((item) => (item.id === itemId ? updated : item)))
    } catch (err) {
      console.error('Failed to toggle wish item purchase state', err)
      setActionError(err instanceof Error ? err.message : 'Unable to update the selected item right now.')
    } finally {
      setTogglingItemId(null)
    }
  }

  const updateCommentInput = (itemId: string, value: string) => {
    setCommentInputs((current) => ({ ...current, [itemId]: value }))
    setCommentErrors((current) => {
      if (!current[itemId]) {
        return current
      }
      return { ...current, [itemId]: null }
    })
  }

  const handleCommentVisibilityChange = (itemId: string, visible: boolean) => {
    setCommentVisibility((current) => ({ ...current, [itemId]: visible }))
  }

  const handleSubmitComment = async (itemId: string) => {
    const trimmedText = (commentInputs[itemId] ?? '').trim()
    if (!trimmedText) {
      setCommentErrors((current) => ({ ...current, [itemId]: 'Comment text is required.' }))
      return
    }

    const visibleToOwner = commentVisibility[itemId] ?? true
    setCommentSubmittingId(itemId)
    setCommentErrors((current) => ({ ...current, [itemId]: null }))

    try {
      const createdComment = await createWishComment(itemId, {
        text: trimmedText,
        commenterId: user!.id!!,
        visibleToOwner
      })

      setCommentsByItemId((current) => {
        const next = { ...current }
        const existing = next[itemId] ? [...next[itemId]!] : []
        existing.push(createdComment)
        existing.sort((a, b) => {
          const aTime = a.createdAt ?? 0
          const bTime = b.createdAt ?? 0
          return aTime - bTime
        })
        next[itemId] = existing
        return next
      })

      setCommentInputs((current) => ({ ...current, [itemId]: '' }))
      setCommentVisibility((current) => ({ ...current, [itemId]: true }))
    } catch (err) {
      console.error('Failed to submit comment', err)
      setCommentErrors((current) => ({
        ...current,
        [itemId]: err instanceof Error ? err.message : 'Unable to add a comment right now.'
      }))
    } finally {
      setCommentSubmittingId(null)
    }
  }

  const getVisibleCommentsForItem = React.useCallback(
    (itemId: string) => {
      const itemComments = commentsByItemId[itemId] ?? []
      if (!isOwnList) {
        return itemComments
      }

      return itemComments.filter((comment) => comment.visibleToOwner !== false)
    },
    [commentsByItemId, isOwnList]
  )

  let listContent: React.ReactNode
  const canSeePurchaseStatus = !isOwnList
  const regularItems = wishItems.filter((item) => !item.isSuggestion)
  const suggestionItems = isOwnList ? [] : wishItems.filter((item) => Boolean(item.isSuggestion))

  if (loading) {
    listContent = (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading wish list...</p>
      </div>
    )
  } else if (error) {
    listContent = (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: '#c00', fontSize: '1.1rem' }}>{error}</p>
      </div>
    )
  } else if (regularItems.length + suggestionItems.length === 0) {
    listContent = (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>
          No items in this wish list yet. Add some items to get started!
        </p>
      </div>
    )
  } else {

    const ownerDisplayName = displayUser?.shortName ?? 'Someone'
    const renderCard = (item: WishItem) => (
      <WishItemCard
        key={item.id}
        item={item}
        isOwnList={isOwnList}
        canSeePurchaseStatus={canSeePurchaseStatus}
        currentUserId={user?.id ?? null}
        ownerDisplayName={ownerDisplayName}
        isDeleting={deletingItemId === item.id}
        isToggling={togglingItemId === item.id}
        onTogglePurchased={handleTogglePurchased}
        onDeleteItem={handleDeleteItem}
        resolveDisplayName={resolveDisplayName}
        formatCreatedTime={formatCreatedTime}
        comments={getVisibleCommentsForItem(item.id)}
        commentsLoading={commentsLoading}
        commentsError={commentsError}
        commentInput={commentInputs[item.id] ?? ''}
        commentError={commentErrors[item.id] ?? null}
        onCommentInputChange={updateCommentInput}
        commentVisibleToOwner={commentVisibility[item.id] ?? false}
        onCommentVisibilityChange={handleCommentVisibilityChange}
        onSubmitComment={handleSubmitComment}
        isCommentSubmitting={commentSubmittingId === item.id}
      />
    )

    if (regularItems.length === 0 && (suggestionItems.length === 0 && !isOwnList)) {
      listContent = (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: '#666', fontSize: '1.1rem' }}>
            No items in this wish list yet. Add some items to get started!
          </p>
        </div>
      )
    } else {
      listContent = (
        <>
          {regularItems.length > 0 && (
            <section key="wish-items" style={{ display: 'grid', gap: '0.75rem' }}>
              <div>
                <h2 style={{ margin: 0, color: 'var(--primary-color)' }}>Wish List</h2>
              </div>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {regularItems.map((item) => renderCard(item))}
              </div>
            </section>
          )}
          {suggestionItems.length > 0 && !isOwnList && (
            <section key="suggestions" style={{ display: 'grid', gap: '0.75rem' }}>
              <div>
                <h2 style={{ margin: 0, color: '#a34f0b' }}>Suggestions</h2>
              </div>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {suggestionItems.map((item) => renderCard(item))}
              </div>
            </section>
          )}
        </>
      )
    }
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ color: 'var(--primary-color)' }}>
          {`${displayName}'s Wish List`}
        </h1>
        {canSubmitItems && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading || !userId?.trim()}
            onClick={handleToggleAddForm}
          >
            <Plus size={16} style={{ marginRight: '0.5rem' }} />
            {isAddFormOpen ? 'Close' : isOwnList ? 'Add Item' : 'Suggest Item'}
          </button>
        )}
      </div>

      {canSubmitItems && isAddFormOpen && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <form onSubmit={handleAddItemSubmit} style={{ display: 'grid', gap: '1rem' }}>
            <div style={{ display: 'grid', gap: '0.35rem' }}>
              <label htmlFor="wish-name" style={{ fontWeight: 600 }}>{isOwnList ? 'Item name' : 'Suggestion name'}</label>
              <input
                id="wish-name"
                type="text"
                value={newItemName}
                onChange={(event) => setNewItemName(event.target.value)}
                placeholder={itemPlaceholder}
                required
                disabled={isSubmitting}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ display: 'grid', gap: '0.35rem' }}>
              <label htmlFor="wish-description" style={{ fontWeight: 600 }}>Description</label>
              <textarea
                id="wish-description"
                value={newItemDescription}
                onChange={(event) => setNewItemDescription(event.target.value)}
                rows={3}
                disabled={isSubmitting}
                placeholder="Share any helpful details"
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'grid', gap: '0.35rem' }}>
              <label htmlFor="wish-url" style={{ fontWeight: 600 }}>Link URL</label>
              <input
                id="wish-url"
                type="url"
                value={newItemUrl}
                onChange={(event) => setNewItemUrl(event.target.value)}
                disabled={isSubmitting}
                placeholder="https://example.com/product"
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ display: 'grid', gap: '0.35rem' }}>
              <label htmlFor="wish-image-url" style={{ fontWeight: 600 }}>Image URL</label>
              <input
                id="wish-image-url"
                type="url"
                value={newItemImageUrl}
                onChange={(event) => setNewItemImageUrl(event.target.value)}
                disabled={isSubmitting}
                placeholder="https://example.com/photo.jpg"
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
              />
            </div>
            {!isOwnList && (
              <p style={{ color: '#555', margin: 0 }}>
                Suggestions are not shared with {displayName}.
              </p>
            )}
            {formError && (
              <p style={{ color: '#c00', margin: 0 }}>{formError}</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              {!isSubmitting && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleToggleAddForm}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || !newItemName.trim()}
              >
                {isSubmitting ? 'Saving...' : 'Save Item'}
              </button>
            </div>
          </form>
        </div>
      )}

      {actionError && (
        <div className="card" style={{ marginBottom: '1rem', background: '#fff6f6', border: '1px solid #f5c2c7' }}>
          <p style={{ color: '#c00', margin: 0 }}>{actionError}</p>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {listContent}
      </div>
    </div>
  )
}

export default WishList